import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import {
  Select2Option,
  Select2UpdateEvent,
  Select2UpdateValue,
} from 'ng-select2-component';
import { Observable } from 'rxjs';
import { RouteService } from 'src/app/core/services/route.service';
import { LoadFields } from 'src/app/core/state/_actions/field.actions';
import { LoadFormSavedFields } from 'src/app/core/state/_actions/form.actions';
import { DROPDOWN_VALUE_PLACEHOLDER, NO_OPTION_TO_SELECT, SELECT_LOADER } from 'src/app/shared/constants';
import { LaunchDarklyService } from 'src/app/core/services/launchdarkly.service';
import { FEATURE_FLAGS } from 'src/app/shared/launchdarkly.constants';
import { LDFlagValue } from 'launchdarkly-js-client-sdk';
import { SubSink } from 'subsink';
@Component({
  selector: 'app-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  host: {
    '[class.accessibility-enabled]': 'enableDataTableAccessibilityValue'
  }
})
export class SelectComponent implements OnInit, OnChanges {
  @Input('isLoading') isLoading:boolean = false;
  @Input('isError') isError:boolean = false;
  @Input('form') form!: UntypedFormGroup;
  @Input('label') label!: string;
  @Input('infoText') infoText!: string;
  @Input('options') options: Array<any> | null = null;
  @Input('value') value: Select2UpdateValue = [];
  @Input('error') error: string = 'This field is required';
  @Input('controlName') controlName: string = '';
  @Input('multiple') multiple: boolean = false;
  @Input('disabled') disabled: boolean = false;
  @Input('canClear') canClear: boolean = false;
  @Output('selected') selected = new EventEmitter<Array<Select2Option>>();
  @Output('clear') clear = new EventEmitter<void>();
  @Input('nullPlaceholder') nullPlaceholder: string = SELECT_LOADER;
  @Input('customPlaceholder') customPlaceholder: string = '';
  @Input() requiredMark: boolean = false;
  @Input('key') key: string = '';
  @Input('isRelation') isRelation: boolean = false;
  @Input('fieldName') fieldName: string = '';
  @Input('relatedTableId') relatedTableId: string = '';
  @Input('relatedFormId') relatedFormId: string = '';
  @Input('modalLayer') modalLayer: number = 0;
  @Input('currentIndex') currentIndex: number = -1;
  @Input('fromAction') fromAction:boolean = false;
  @Output('saveDataWithIndex') saveDataWithIndex = new EventEmitter();
  @Output('selectOpened') selectOpened = new EventEmitter();
  @Output('selectClosed') selectClosed = new EventEmitter();
  @Input('IsFromForm') IsFromForm:boolean = false;
  @Input('IsInfoVisible') IsInfoVisible:boolean = true;
  @Input('DisplayOnScreen') DisplayOnScreen:boolean = false;
  @Input('AllowMinimize') AllowMinimize:boolean = false;
  @Input('IsRowDisplayOnScreen') IsRowDisplayOnScreen:boolean = false;
  @Input('selectedValue') selectedValue:any = null;
  @Input('accessibleLabel') accessibleLabel: string = 'Select option';

  isTextVisible:boolean = true
  required = false;
  selectId: string = '';
  errorId: string = '';

  /**
   * True if select is being used from actions.component.ts, need to change first option
   * as 'Add item' where item is passed label
   */
  @Input('isFormAction') isFormAction: boolean = false;
  @Input('defaultSelectionLabel') defaultSelectionLabel: string | null = null;

  @Input('isHeader') isHeader = false;
  @Input('IsFormFieldNameHidden') IsFormFieldNameHidden = false;
  @Input('toShowRedAsteriskWhenDisabled') toShowRedAsteriskWhenDisabled = false;

  validator = Validators.required;
  
  // Feature flag for accessibility
  enableDataTableAccessibility$!: Observable<LDFlagValue>;
  enableDataTableAccessibilityValue: boolean = false;
  private subsink: SubSink = new SubSink();

  constructor(
    private modal: NgbModal,
    private store: Store,
    private route: RouteService,
    private readonly ldService: LaunchDarklyService
  ) {}

  ngOnChanges(changes: any) {
    const disabled = changes?.disabled?.currentValue;
    // do not change below boolean check
    if (this.control?.errors?.required === true ) this.required = true;
    else this.required = false;
    if (disabled === false){
      this.control.enable();
    } else if (disabled === true){
      this.required = false;
      this.control.disable();
    }
  }

  ngOnInit(): void {
    // Initialize feature flag observable
    this.enableDataTableAccessibility$ = this.ldService.getFlagValue(
      FEATURE_FLAGS.ENABLE_DATA_TABLE_ACCESSIBILITY,
      false
    );
    
    // Subscribe to get synchronous value for logic
    this.subsink.add(
      this.enableDataTableAccessibility$.subscribe((value) => {
        this.enableDataTableAccessibilityValue = !!value;
      })
    );

    // Generate unique IDs for accessibility - only when flag is ON
    if (this.enableDataTableAccessibilityValue) {
      this.selectId = `select-${Math.random().toString(36).substring(2, 11)}`;
      this.errorId = `error-${this.selectId}`;
    }

    if (this.control?.errors?.required === true) this.required = true;
  }

  get control() {
    return this.form?.controls?.[this.controlName] || ({} as UntypedFormControl);
  }

  /**
   * Get state if data is loaded or not
   * @returns true if loading
   */
  isOptionLoading = () => this.options === null;

  /**
   * Options for select
   * @returns options
   */
  getOptions = () => {
    // let options = [];
    // if(this.options && this.multiple){
    //   options = this.options
    // }
    // else if(!this.isLoading && this.options?.length ) {
    //   options = [
    //     {
    //       value: this.isFormAction ? '' : null,
    //       label: DROPDOWN_VALUE_PLACEHOLDER,
    //     },
    //     ...this.options,
    //   ]
    // }
    // else if(!this.isLoading && !this.options?.length){
    //   options = [
    //     {
    //       value: this.isFormAction ? '' : null,
    //       label: NO_OPTION_TO_SELECT,
    //     },
    //   ]
    // }

    const options = this.options?.length
    ? this.multiple
      ? this.options
        : [
          {
            value: this.isFormAction ? '' : null,
            label: DROPDOWN_VALUE_PLACEHOLDER,
          },
          ...this.options,
        ]
    : [];

    return options;
  };

  /**
   * Placeholder 'type here' if options is null
   * @returns placeholder
   */
  getPlaceholder = () =>{
    return this.isLoading && (this.isOptionLoading() || !this.options?.length)?
      this.nullPlaceholder
      : this.isError ?
        NO_OPTION_TO_SELECT
      : this.customPlaceholder
        ? this.customPlaceholder
        : this.multiple
        ? 'Type here'
        : !this.isLoading  && this.isOptionLoading()? '': !this.isLoading  && !this.options?.length? NO_OPTION_TO_SELECT: DROPDOWN_VALUE_PLACEHOLDER;
    }
  /**
   * On change event
   */
  change = (event: Select2UpdateEvent<Select2UpdateValue>) => {
    const data = { data: event.options, index: this.currentIndex };
    this.saveDataWithIndex.emit(data);
    if (event && event.options && event.options.some(option => option.value === 'click!')) {
      this.openModal();
      this.control?.setValue(this.isFormAction ? '' : null);
    }
    this.selected.emit(event.options);
    if (this.disabled) this.control?.disable();
  };

  clearSelect = () => {
    this.clear.emit();
  };
  /**
   * Set errors here priority wise
   * @return error message
   */
  getErrorMessage = () => {
    if (this.control?.hasError('required')) return this.error;
    return '';
  };

  /**
   * Open popup for adding a table
   */
  openModal = async (opType: string = '') => {
    document.body.click();
    this.store.dispatch(new LoadFields(this.route.getTableKey()));
    const { UpsertFieldComponent } = await import(
      '../../../../modules/application/field/upsert/upsert.component'
    );
    const modalRef = this.modal.open(UpsertFieldComponent, {
      windowClass: 'modal-custom',
      centered: true,
      size: 'xl',
      backdrop: 'static',
      animation: true,
      keyboard: false,
    });
    modalRef.componentInstance.key = this.key;
    modalRef.componentInstance.fieldName = this.fieldName;
    modalRef.result.then(() => {
      this.reloadFormSavedFields();
    }).catch(() => {
      //handle dismissals
      this.reloadFormSavedFields();
    });
  }

  private readonly reloadFormSavedFields = () => {
    this.store.dispatch(
      new LoadFormSavedFields(
        this.route.getTableKey(),
        this.route.paramMap.formId
      )
    );
  };

  toggleText(): void {
    this.isTextVisible = !this.isTextVisible;
  }

  handleToggleKeydown(event: KeyboardEvent): void {
    // Only handle keyboard events when accessibility flag is ON
    if (this.enableDataTableAccessibilityValue) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.toggleText();
      }
    }
  }

  handleClearKeydown(event: KeyboardEvent): void {
    // Only handle keyboard events when accessibility flag is ON
    if (this.enableDataTableAccessibilityValue) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.clearSelect();
      }
    }
  }
  
  ngOnDestroy(): void {
    this.subsink.unsubscribe();
  }
}
